import { Button, InputNumber } from "@sellar-io/components";
import { useState } from "react";
import { SpinnerLegacy } from "../../../../components";
import { Alert } from "../../../../components/alert/Alert";
import { InputLabel, InputContainer } from "@sellar-io/components";
import { DialogCTA } from "../../../../components/modal/Dialog";
import {
  TableLegacy,
  TableLegacyContainer,
  TableLegacyTHead,
  TableLegacyTR,
  TableLegacyTH,
  TableLegacyTBody,
} from "../../../../components/table/TableLegacy";
import { apiV2AssertError } from "../../../../hooks/api/v2/apiV2Request";
import { usePriceTierList } from "../../../../hooks/api/v2/priceTier/usePriceTierList";
import { useSupplierProduct } from "../../../../hooks/api/v2/product/useSupplierProduct";
import { useProductUpdate } from "../../../../hooks/api/v2/product/useProductUpdate";
import { useProductPriceTierBatchCreate } from "../../../../hooks/api/v2/productPriceTier/useProductPriceTierBatchCreate";
import { ProductCategoryItemSwitches } from "../v2/ProductCategory/ProductCategoryItemV2/ProductCategoryItemSwitches";
import { ProductItemDialogPages } from "./ProductItemDialog.shared";
import ProductItemDialogPricingTableRow from "./ProductItemDialogPricingTableRow";
import { useRemoteFeatureFlags } from "../../../../hooks/useRemoteFeatureFlags";

const MaxOrderQuantityInput = ({
  loading,
  maxOrderQuantity,
  onChangeMaxOrderQuantity,
}: {
  loading: boolean;
  maxOrderQuantity: number | null;
  onChangeMaxOrderQuantity: (newValue: number | null) => void;
}) => {
  return (
    <div>
      <InputLabel>Maximum order quantity</InputLabel>
      <InputContainer className={"w-full"}>
        <InputNumber
          disabled={loading}
          value={maxOrderQuantity ?? undefined}
          onChange={(e) => {
            const value = parseInt(e.target.value, 10);
            onChangeMaxOrderQuantity(isNaN(value) ? null : value);
          }}
        />
      </InputContainer>
    </div>
  );
};

export type priceTierItemsType = {
  basePrice: number | null;
  price: number;
  PriceTierId: number;
  ProductId: number;
  active: boolean;
  discountable: boolean;
}[];
export default function ProductItemDialogPricing({
  onDismiss,
  productId,
  parentActive,
  setStep,
}: {
  productId: number;
  parentActive: boolean;
  setStep: (step: number) => void;
  onDismiss: (productId: number) => void;
}) {
  const remoteFeatureFlag = useRemoteFeatureFlags();
  const productQuery = useSupplierProduct(productId);
  const [stock, setStock] = useState<number>();
  const [maxOrderQuantity, setMaxOrderQuantity] = useState<number | null>(null);
  const [skipAttempts, setSkipAttempts] = useState<number>(0);
  const [warning, setWarning] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [updatedPriceTierItems, setUpdatedPriceTierItems] =
    useState<priceTierItemsType>();
  const productPriceTierBatchCreateMutation = useProductPriceTierBatchCreate();
  const productUpdateMutation = useProductUpdate(productId);
  const product = productQuery.data;
  const priceTierListQuery = usePriceTierList({
    limit: 50,
    query: [
      {
        type: "ProductId",
        modifier: "EQUALS",
        value: productId,
      },
    ],
  });

  const closeDialog = () => {
    setLoading(false);
    onDismiss(productId);
    setSkipAttempts(0);
    setWarning(false);
    setStep(ProductItemDialogPages.PRODUCT_DETAILS);
  };
  const onSubmit = () => {
    if (!stock && !updatedPriceTierItems?.length) return closeDialog();
    setLoading(true);
    return productUpdateMutation.mutate(
      {
        stock: stock ?? 0,
        maxOrderQuantity,
      },
      {
        onSuccess: () => {
          if (!!updatedPriceTierItems?.length) {
            return productPriceTierBatchCreateMutation.mutate(
              updatedPriceTierItems,
              {
                onSuccess: () => closeDialog(),
              },
            );
          }
          closeDialog();
        },
      },
    );
  };
  if (!product) return <SpinnerLegacy />;
  return (
    <div className={"flex flex-col gap-4"}>
      {productUpdateMutation.isError ? (
        <Alert
          type={"error"}
          message={
            apiV2AssertError(productUpdateMutation.error)?.response?.data
              .message ?? "Unknown error"
          }
        />
      ) : null}
      {productPriceTierBatchCreateMutation.isError ? (
        <Alert
          type={"error"}
          message={
            apiV2AssertError(productPriceTierBatchCreateMutation.error)
              ?.response?.data.message ?? "Unknown error"
          }
        />
      ) : null}
      <InputLabel>Stock total</InputLabel>
      <InputContainer className={"w-full"}>
        <InputNumber
          required={true}
          disabled={loading}
          value={stock}
          onChange={(e) => setStock(parseInt(e.target.value, 10))}
        />
      </InputContainer>
      {remoteFeatureFlag.isActive("preOrders") && (
        <MaxOrderQuantityInput
          loading={loading}
          maxOrderQuantity={maxOrderQuantity}
          onChangeMaxOrderQuantity={(newMaxOrderQuantity) => {
            setMaxOrderQuantity(newMaxOrderQuantity);
          }}
        />
      )}
      {product && (
        <ProductCategoryItemSwitches
          product={product}
          parentActive={parentActive}
          className={"flex flex-col divide-y"}
        />
      )}
      <div className="flex flex-col gap-2">
        <InputLabel>Pricing</InputLabel>
        {warning && (
          <Alert type={"warning"} title={"Pricing missing"} message={""}>
            <p>
              Add pricing for <strong>at least one</strong> of your customer
              groups to list this product on your storefront
            </p>
          </Alert>
        )}
        <TableLegacyContainer className={"max-h-[300px]"}>
          <TableLegacy>
            <TableLegacyTHead>
              <TableLegacyTR>
                <TableLegacyTH className={"sticky top-0"}>
                  Price tier
                </TableLegacyTH>
                <TableLegacyTH className={"sticky top-0"}>
                  Unit price
                </TableLegacyTH>
              </TableLegacyTR>
            </TableLegacyTHead>
            <TableLegacyTBody>
              {priceTierListQuery.data?.pages.map((page) =>
                page.map((priceTier) => (
                  <ProductItemDialogPricingTableRow
                    key={priceTier.id}
                    priceTier={priceTier}
                    setUpdatedPriceTierItems={setUpdatedPriceTierItems}
                    updatedPriceTierItems={updatedPriceTierItems}
                    productId={productId}
                    warning={warning}
                  />
                )),
              )}
            </TableLegacyTBody>
          </TableLegacy>
        </TableLegacyContainer>
      </div>
      <DialogCTA>
        <Button
          color={"tertiary"}
          onClick={() => {
            if (skipAttempts === 0) {
              setSkipAttempts(1);
              return setWarning(true);
            }
            closeDialog();
          }}
        >
          Skip for now
        </Button>
        <Button
          loading={loading}
          onClick={() => {
            if (!updatedPriceTierItems?.length && skipAttempts === 0) {
              setSkipAttempts(1);
              return setWarning(true);
            }
            onSubmit();
          }}
        >
          Confirm
        </Button>
      </DialogCTA>
    </div>
  );
}
